/**
 * How often «печатает…» is re-asserted while someone keeps typing.
 *
 * The receiver expires the indicator rather than waiting to be told it ended,
 * so this is a keepalive: it has to be comfortably shorter than TYPING_TTL_MS
 * or the indicator would blink out between keystrokes.
 */
export const TYPING_HEARTBEAT_MS = 3_000;

/** Stop typing after this long without a keystroke, as WhatsApp does. */
export const TYPING_IDLE_MS = 5_000;

/**
 * How long a received «печатает…» survives without being re-asserted.
 *
 * This is the whole reason the indicator cannot get stuck: a phone that is
 * locked mid-word never delivers its `false`, and its socket can outlive the
 * app by a heartbeat interval. Only a claim that stops being renewed can be
 * told apart from one that is still true — the same lesson the foreground
 * claim in server/src/realtime.ts is built around.
 */
export const TYPING_TTL_MS = 8_000;

/**
 * Turns keystrokes into the two events that actually go over the socket.
 *
 * No timers and no socket of its own: the caller drives it, which is what makes
 * the decisions here testable without waiting out real seconds.
 */
export class TypingReporter {
  private active = false;
  private lastKeystrokeAt = 0;

  constructor(
    private readonly send: (typing: boolean) => void,
    private readonly now: () => number = Date.now,
  ) {}

  /** Called on every change to the message field. */
  keystroke(): void {
    this.lastKeystrokeAt = this.now();
    if (this.active) return;
    this.active = true;
    this.send(true);
  }

  /** Called on TYPING_HEARTBEAT_MS: renews the claim, or lets it go. */
  tick(): void {
    if (!this.active) return;
    if (this.now() - this.lastKeystrokeAt >= TYPING_IDLE_MS) {
      this.stop();
      return;
    }
    this.send(true);
  }

  /** Called when the message is sent, the chat changes, or the view goes away. */
  stop(): void {
    if (!this.active) return;
    this.active = false;
    this.send(false);
  }

  get isActive(): boolean {
    return this.active;
  }
}
